import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ChevronLeft, ChevronRight, Play, X, GraduationCap, Radio,
  Copy, Check, Edit3, Trash2, Plus, Save, ArrowUp, ArrowDown, FileText, Users, BookOpen,
} from 'lucide-react';
import { DECKS as BASE_DECKS } from '@/lib/teachersCornerDecks';
import { getToken } from '@/utils/localStorage';

const BG = '#141410';
const SURFACE = '#1E1E1A';
const SURFACE2 = '#28281F';
const BORDER = '#2E2E2A';
const CLAY = '#C45A3B';
const CREAM = '#F5F2EB';
const DIM = 'rgba(245,242,235,0.55)';
const MUTED = 'rgba(245,242,235,0.35)';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const cloneDeckForEdit = (deck) => ({
  name: '',
  sourceDeckId: deck.id,
  tagline: deck.tagline || '',
  slides: (deck.slides || []).map((s) => ({
    title: s.title || '',
    subtitle: s.subtitle || '',
    items: [...(s.items || [])],
  })),
});

// ─── Live session banner ─────────────
const LiveBanner = ({ session, onEnd, copied, onCopy }) => (
  <div data-testid="tc-live-banner"
    style={{
      display: 'flex', alignItems: 'center', gap: 14, padding: '10px 14px',
      background: 'rgba(196,90,59,0.12)', border: `1px solid ${CLAY}55`,
      borderRadius: 12, marginBottom: 22, flexWrap: 'wrap',
    }}>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: CLAY, fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700 }}>
      <Radio size={12} /> Live
    </span>
    <div style={{ fontSize: 13, color: DIM }}>
      Students join at <span style={{ color: CREAM }}>Student&apos;s Lounge</span> with code:
    </div>
    <div style={{
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: 22, letterSpacing: '0.3em', color: CREAM, fontWeight: 700,
      padding: '4px 12px', background: SURFACE2, borderRadius: 8, border: `1px solid ${BORDER}`,
    }} data-testid="tc-session-code">
      {session.code}
    </div>
    <button onClick={onCopy} data-testid="tc-copy-code"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'transparent',
        border: `1px solid ${BORDER}`, color: DIM, padding: '6px 10px', borderRadius: 999, fontSize: 11, cursor: 'pointer' }}>
      {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
    </button>
    <div style={{ flex: 1 }} />
    <button onClick={onEnd} data-testid="tc-end-session"
      style={{ background: 'transparent', border: `1px solid ${BORDER}`, color: DIM,
        padding: '6px 12px', borderRadius: 999, fontSize: 12, cursor: 'pointer' }}>
      End session
    </button>
  </div>
);

// ─── Deck picker ─────────────
const DeckCard = ({ deck, sourceDeck, isCustom, onOpen, onEdit, onDelete, testId }) => (
  <div style={{
    background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: 20,
    color: CREAM, display: 'flex', flexDirection: 'column', gap: 10, minHeight: 180,
  }} data-testid={testId}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = CLAY; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = BORDER; }}
  >
    <div style={{ fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: CLAY, fontWeight: 600 }}>
      {isCustom ? `Custom · from ${sourceDeck?.title || 'unknown'}` : 'Base deck'}
    </div>
    <div style={{ fontFamily: '"DM Serif Display", Georgia, serif', fontSize: 24, lineHeight: 1.1 }}>
      {deck.title || deck.name || 'Untitled'}
    </div>
    <div style={{ fontSize: 13, color: DIM, lineHeight: 1.5, flex: 1 }}>
      {deck.tagline || (isCustom ? 'Your saved version.' : '')}
    </div>
    <div style={{ fontSize: 11, color: MUTED }}>{(deck.slides || []).length} slides</div>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <button onClick={onOpen} data-testid={`${testId}-present`}
        style={{
          background: CLAY, border: 0, borderRadius: 999, padding: '7px 14px',
          color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 5,
        }}>
        <Play size={12} fill="currentColor" /> Present
      </button>
      <button onClick={onEdit} data-testid={`${testId}-edit`}
        style={{
          background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 999,
          padding: '7px 12px', color: DIM, fontSize: 12, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 5,
        }}>
        <Edit3 size={12} /> {isCustom ? 'Edit' : 'Copy & edit'}
      </button>
      {isCustom && (
        <button onClick={onDelete} data-testid={`${testId}-delete`}
          style={{
            background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 999,
            padding: '7px 10px', color: DIM, fontSize: 12, cursor: 'pointer',
          }} title="Delete this version">
          <Trash2 size={12} />
        </button>
      )}
    </div>
  </div>
);

const DeckPicker = ({
  onOpenBase, onOpenCustom, onEditBase, onEditCustom, onDeleteCustom, onBack,
  session, onStartSession, onEndSession, copied, onCopy, customDecks, loading,
}) => (
  <div data-ol-dark style={{ minHeight: '100vh', background: BG, color: CREAM, padding: 24, paddingTop: 'calc(24px + env(safe-area-inset-top, 0px))' }}>
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <button onClick={onBack} data-testid="tc-back"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent',
          border: 0, color: DIM, cursor: 'pointer', marginBottom: 18, fontSize: 13 }}>
        <ArrowLeft size={16} /> Dashboard
      </button>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <GraduationCap size={16} color={CLAY} />
        <span style={{ fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase', color: CLAY, fontWeight: 600 }}>
          Teacher&apos;s Corner
        </span>
      </div>
      <h1 style={{ fontFamily: '"DM Serif Display", Georgia, serif', fontSize: 44, lineHeight: 1.05, marginBottom: 8 }}>
        Present the app.
      </h1>
      <p style={{ color: DIM, fontSize: 15, lineHeight: 1.6, maxWidth: 620, marginBottom: 24 }}>
        Launch the live drill for students in the lounge, or open the field guide beside your workshop.
      </p>

      {session ? (
        <LiveBanner session={session} onEnd={onEndSession} copied={copied} onCopy={onCopy} />
      ) : (
        <button onClick={onStartSession} data-testid="tc-start-session"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: CLAY, border: 0, borderRadius: 999,
            padding: '10px 18px', color: '#fff', fontWeight: 600, fontSize: 13,
            cursor: 'pointer', marginBottom: 22,
          }}>
          <Radio size={14} /> Start live session
        </button>
      )}

      {/* Operation Listen — live 4-panel demo, launched straight from Teacher's Corner */}
      <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: CLAY, fontWeight: 600, marginBottom: 10 }}>
        Live drill
      </div>
      <button
        onClick={() => {
          const qs = session?.code ? `?tcSession=${session.code}` : '';
          window.location.assign('/admin/operation-listen' + qs);
        }}
        data-testid="tc-olg-demo-card"
        style={{
          display: 'block', width: '100%', maxWidth: 640, textAlign: 'left',
          background: `linear-gradient(135deg, ${SURFACE2} 0%, ${SURFACE} 100%)`,
          border: `1.5px solid ${CLAY}55`,
          borderRadius: 16, padding: 22, cursor: 'pointer',
          transition: 'border-color .2s ease, transform .2s ease',
          marginBottom: 30,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = CLAY; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${CLAY}55`; }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: `${CLAY}22`, border: `1px solid ${CLAY}55`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Users size={22} color={CLAY} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: CLAY, fontWeight: 600, marginBottom: 3 }}>
              Operation Listen · Demo room
            </div>
            <div style={{ fontFamily: '"DM Serif Display", Georgia, serif', fontSize: 22, lineHeight: 1.1, color: CREAM, marginBottom: 6 }}>
              Launch the live drill.
            </div>
            <div style={{ fontSize: 13, color: DIM, lineHeight: 1.5 }}>
              Spin up a demo room with all four roles on one screen — Coach, Guide, Conflict, Conflict Coach.
              Use "Focus one" to test a single seat, or leave it in "Show all" to walk the class through a full round.
              {session ? ' Students in the lounge will watch the round live.' : ''}
            </div>
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 999, background: CLAY, color: '#fff',
            fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', flexShrink: 0,
          }}>
            <Play size={12} /> Open
          </div>
        </div>
      </button>

      {/* Reference — self-contained HTML docs for facilitators */}
      <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: CLAY, fontWeight: 600, marginBottom: 10 }}>
        Reference
      </div>
      <a
        href="/modules/training-day-field-guide.html"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="tc-field-guide-card"
        style={{
          display: 'block', width: '100%', maxWidth: 640, textDecoration: 'none',
          background: `linear-gradient(135deg, ${SURFACE2} 0%, ${SURFACE} 100%)`,
          border: `1.5px solid ${BORDER}`,
          borderRadius: 16, padding: 22,
          transition: 'border-color .2s ease',
          marginBottom: 30,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = CLAY; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = BORDER; }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: `${CLAY}22`, border: `1px solid ${CLAY}55`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <BookOpen size={22} color={CLAY} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: CLAY, fontWeight: 600, marginBottom: 3 }}>
              Field guide
            </div>
            <div style={{ fontFamily: '"DM Serif Display", Georgia, serif', fontSize: 22, lineHeight: 1.1, color: CREAM, marginBottom: 6 }}>
              Training Day Field Guide.
            </div>
            <div style={{ fontSize: 13, color: DIM, lineHeight: 1.5 }}>
              The single-page facilitator handbook. Open in a new tab and keep it beside the deck as you present.
            </div>
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 999, background: 'transparent',
            border: `1px solid ${CLAY}66`, color: CLAY,
            fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', flexShrink: 0,
          }}>
            Open ↗
          </div>
        </div>
      </a>
    </div>
  </div>
);

// ─── Editor ─────────────
const SlideEditor = ({ slide, index, total, onChange, onDelete, onMove }) => {
  const setField = (k, v) => onChange({ ...slide, [k]: v });
  const setItem = (i, v) => {
    const items = [...(slide.items || [])];
    items[i] = v;
    onChange({ ...slide, items });
  };
  const addItem = () => onChange({ ...slide, items: [...(slide.items || []), ''] });
  const removeItem = (i) => {
    const items = (slide.items || []).filter((_, idx) => idx !== i);
    onChange({ ...slide, items });
  };
  return (
    <div style={{
      background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12,
      padding: 16, marginBottom: 12,
    }} data-testid={`tc-edit-slide-${index}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: CLAY, fontWeight: 600 }}>
          Slide {index + 1} of {total}
        </span>
        <div style={{ flex: 1 }} />
        <button onClick={() => onMove(-1)} disabled={index === 0}
          style={{ background: 'transparent', border: `1px solid ${BORDER}`, color: DIM, borderRadius: 8, padding: '4px 8px', cursor: index === 0 ? 'not-allowed' : 'pointer', opacity: index === 0 ? 0.3 : 1 }}
          data-testid={`tc-edit-slide-${index}-up`} title="Move up"><ArrowUp size={12} /></button>
        <button onClick={() => onMove(1)} disabled={index === total - 1}
          style={{ background: 'transparent', border: `1px solid ${BORDER}`, color: DIM, borderRadius: 8, padding: '4px 8px', cursor: index === total - 1 ? 'not-allowed' : 'pointer', opacity: index === total - 1 ? 0.3 : 1 }}
          data-testid={`tc-edit-slide-${index}-down`} title="Move down"><ArrowDown size={12} /></button>
        <button onClick={onDelete}
          style={{ background: 'transparent', border: `1px solid ${BORDER}`, color: DIM, borderRadius: 8, padding: '4px 8px', cursor: 'pointer' }}
          data-testid={`tc-edit-slide-${index}-delete`} title="Delete slide"><Trash2 size={12} /></button>
      </div>
      <label style={{ display: 'block', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>Title</label>
      <input type="text" value={slide.title || ''} onChange={(e) => setField('title', e.target.value)}
        data-testid={`tc-edit-slide-${index}-title`}
        style={{ width: '100%', background: SURFACE2, border: `1px solid ${BORDER}`, color: CREAM, borderRadius: 8, padding: '8px 10px', fontSize: 15, marginBottom: 10, outline: 'none' }} />
      <label style={{ display: 'block', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>Subtitle</label>
      <input type="text" value={slide.subtitle || ''} onChange={(e) => setField('subtitle', e.target.value)}
        data-testid={`tc-edit-slide-${index}-subtitle`}
        style={{ width: '100%', background: SURFACE2, border: `1px solid ${BORDER}`, color: CREAM, borderRadius: 8, padding: '8px 10px', fontSize: 13, marginBottom: 10, outline: 'none' }} />
      <label style={{ display: 'block', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>Reveal items</label>
      {(slide.items || []).map((it, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <textarea value={it} onChange={(e) => setItem(i, e.target.value)}
            data-testid={`tc-edit-slide-${index}-item-${i}`}
            rows={2}
            style={{ flex: 1, background: SURFACE2, border: `1px solid ${BORDER}`, color: CREAM, borderRadius: 8, padding: '6px 10px', fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />
          <button onClick={() => removeItem(i)}
            style={{ background: 'transparent', border: `1px solid ${BORDER}`, color: DIM, borderRadius: 8, padding: '0 8px', cursor: 'pointer', fontSize: 11 }}
            data-testid={`tc-edit-slide-${index}-item-${i}-delete`}><Trash2 size={12} /></button>
        </div>
      ))}
      <button onClick={addItem} data-testid={`tc-edit-slide-${index}-add-item`}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent',
          border: `1px dashed ${BORDER}`, color: DIM, padding: '6px 10px', borderRadius: 8, fontSize: 11, cursor: 'pointer', marginTop: 4 }}>
        <Plus size={11} /> Add item
      </button>
    </div>
  );
};

const Editor = ({ initial, existingId, onCancel, onSaved }) => {
  const [name, setName] = useState(initial.name || '');
  const [tagline, setTagline] = useState(initial.tagline || '');
  const [slides, setSlides] = useState(initial.slides || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const updateSlide = (i, newSlide) => {
    const arr = [...slides];
    arr[i] = newSlide;
    setSlides(arr);
  };
  const addSlide = () => setSlides([...slides, { title: 'New slide', subtitle: '', items: [] }]);
  const deleteSlide = (i) => setSlides(slides.filter((_, idx) => idx !== i));
  const moveSlide = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= slides.length) return;
    const arr = [...slides];
    const [x] = arr.splice(i, 1);
    arr.splice(j, 0, x);
    setSlides(arr);
  };

  const save = async ({ asNew = false } = {}) => {
    setError('');
    if (!name.trim()) {
      setError('Please give this version a name.');
      return;
    }
    setSaving(true);
    try {
      const body = { name: name.trim(), sourceDeckId: initial.sourceDeckId, tagline, slides };
      const res = existingId && !asNew
        ? await axios.put(`${API}/api/tc/decks/${existingId}`, body, { headers: authHeaders() })
        : await axios.post(`${API}/api/tc/decks`, body, { headers: authHeaders() });
      onSaved(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const sourceDeck = BASE_DECKS.find((d) => d.id === initial.sourceDeckId);

  return (
    <div data-ol-dark style={{ minHeight: '100vh', background: BG, color: CREAM }} data-testid="tc-editor">
      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 5,
        display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px',
        paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
        borderBottom: `1px solid ${BORDER}`, background: SURFACE, flexWrap: 'wrap',
      }}>
        <button onClick={onCancel} data-testid="tc-edit-cancel"
          style={{ background: 'transparent', border: 0, color: DIM, cursor: 'pointer', padding: 6 }}>
          <ArrowLeft size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: CLAY, fontWeight: 600 }}>
            {existingId ? 'Editing saved version' : `Copy from ${sourceDeck?.title || 'deck'}`}
          </div>
          <input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Name this version…"
            data-testid="tc-edit-name"
            style={{
              width: '100%', background: 'transparent', border: 0, outline: 'none',
              color: CREAM, fontFamily: '"DM Serif Display", Georgia, serif',
              fontSize: 24, lineHeight: 1.1, padding: '4px 0',
            }} />
        </div>
        {existingId && (
          <button onClick={() => save({ asNew: false })} disabled={saving}
            data-testid="tc-edit-save"
            style={{
              background: CLAY, border: 0, borderRadius: 999, padding: '8px 16px',
              color: '#fff', fontWeight: 600, fontSize: 13, cursor: saving ? 'wait' : 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6, opacity: saving ? 0.6 : 1,
            }}>
            <Save size={13} /> Save
          </button>
        )}
        <button onClick={() => save({ asNew: true })} disabled={saving}
          data-testid="tc-edit-save-as"
          style={{
            background: existingId ? 'transparent' : CLAY, border: existingId ? `1px solid ${BORDER}` : 0,
            borderRadius: 999, padding: '8px 16px',
            color: existingId ? DIM : '#fff', fontWeight: 600, fontSize: 13,
            cursor: saving ? 'wait' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6, opacity: saving ? 0.6 : 1,
          }}>
          <Save size={13} /> {existingId ? 'Save as new copy' : 'Save copy'}
        </button>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px 60px' }}>
        <label style={{ display: 'block', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: MUTED, marginBottom: 6 }}>
          Deck tagline
        </label>
        <input value={tagline} onChange={(e) => setTagline(e.target.value)}
          data-testid="tc-edit-tagline"
          style={{ width: '100%', background: SURFACE, border: `1px solid ${BORDER}`, color: CREAM, borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none', marginBottom: 22 }} />

        {slides.map((s, i) => (
          <SlideEditor key={i} slide={s} index={i} total={slides.length}
            onChange={(v) => updateSlide(i, v)}
            onDelete={() => deleteSlide(i)}
            onMove={(delta) => moveSlide(i, delta)}
          />
        ))}
        <button onClick={addSlide} data-testid="tc-edit-add-slide"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent',
            border: `1px dashed ${BORDER}`, color: DIM, padding: '10px 14px', borderRadius: 12,
            fontSize: 13, cursor: 'pointer', marginTop: 6,
          }}>
          <Plus size={14} /> Add a slide
        </button>

        {error && (
          <div style={{ color: '#E88066', fontSize: 13, marginTop: 16 }} data-testid="tc-edit-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Presentation ─────────────
const Presentation = ({ deck, onClose, session, onBroadcast, onEndSession, copied, onCopy }) => {
  const [slideIdx, setSlideIdx] = useState(0);
  const [revealCount, setRevealCount] = useState(0);
  const slide = deck.slides[slideIdx];
  const totalItems = slide?.items?.length || 0;

  useEffect(() => {
    if (session) onBroadcast(deck, slideIdx, revealCount);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deck.id, slideIdx, revealCount, session?.code]);

  const advance = useCallback(() => {
    if (revealCount < totalItems) {
      setRevealCount((n) => n + 1);
    } else if (slideIdx < deck.slides.length - 1) {
      setSlideIdx((i) => i + 1);
      setRevealCount(0);
    }
  }, [revealCount, totalItems, slideIdx, deck.slides.length]);

  const back = useCallback(() => {
    if (revealCount > 0) {
      setRevealCount((n) => n - 1);
    } else if (slideIdx > 0) {
      const prevIdx = slideIdx - 1;
      setSlideIdx(prevIdx);
      setRevealCount(deck.slides[prevIdx].items?.length || 0);
    }
  }, [revealCount, slideIdx, deck.slides]);

  const resetSlide = () => setRevealCount(0);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'Enter' || e.key === 'PageDown') {
        e.preventDefault(); advance();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault(); back();
      } else if (e.key === 'Escape') {
        e.preventDefault(); onClose();
      } else if (e.key === 'Home') {
        e.preventDefault(); setSlideIdx(0); setRevealCount(0);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advance, back, onClose]);

  const totalSlides = deck.slides.length;
  const atEnd = slideIdx === totalSlides - 1 && revealCount >= totalItems;

  return (
    <div data-ol-dark onClick={advance}
      style={{ minHeight: '100vh', background: BG, color: CREAM,
        display: 'flex', flexDirection: 'column', cursor: 'pointer', userSelect: 'none' }}
      data-testid="tc-presentation"
    >
      <div onClick={(e) => e.stopPropagation()}
        style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px',
          borderBottom: `1px solid ${BORDER}`, background: SURFACE, cursor: 'default', flexWrap: 'wrap' }}>
        <button onClick={onClose} data-testid="tc-close"
          style={{ background: 'transparent', border: 0, color: DIM, cursor: 'pointer', padding: 6 }}>
          <X size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 140 }}>
          <div style={{ fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: CLAY, fontWeight: 600 }}>
            {deck.title}
          </div>
          <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
            Slide {slideIdx + 1} of {totalSlides} · reveal {revealCount}/{totalItems}
          </div>
        </div>
        {session && (
          <span data-testid="tc-live-pill"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: CLAY,
              fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700,
              padding: '4px 10px', background: 'rgba(196,90,59,0.14)', border: `1px solid ${CLAY}55`, borderRadius: 999 }}>
            <Radio size={11} /> Live · {session.code}
            <button onClick={onCopy}
              style={{ background: 'transparent', border: 0, color: CLAY, cursor: 'pointer', padding: 0, marginLeft: 2 }}>
              {copied ? <Check size={11} /> : <Copy size={11} />}
            </button>
          </span>
        )}
        <button onClick={back} disabled={slideIdx === 0 && revealCount === 0}
          data-testid="tc-prev"
          style={{ background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 999,
            padding: '6px 12px', color: DIM, fontSize: 12, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4,
            opacity: slideIdx === 0 && revealCount === 0 ? 0.3 : 1 }}>
          <ChevronLeft size={14} /> Back
        </button>
        <button onClick={advance} disabled={atEnd} data-testid="tc-next"
          style={{ background: CLAY, border: 0, borderRadius: 999,
            padding: '6px 14px', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4, opacity: atEnd ? 0.4 : 1 }}>
          {revealCount < totalItems ? 'Reveal' : 'Next slide'} <ChevronRight size={14} />
        </button>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 36 }}>
        <div style={{ width: '100%', maxWidth: 900 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: CLAY, fontWeight: 600, marginBottom: 12 }}>
            {deck.title} · {slideIdx + 1}/{totalSlides}
          </div>
          <h1 style={{
            fontFamily: '"DM Serif Display", Georgia, serif',
            fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', lineHeight: 1.02, letterSpacing: '-0.015em',
            marginBottom: slide.subtitle ? 12 : 32,
          }}>{slide.title}</h1>
          {slide.subtitle && (
            <div style={{ fontSize: 20, color: DIM, fontStyle: 'italic', marginBottom: 32 }}>
              {slide.subtitle}
            </div>
          )}
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {slide.items?.map((it, i) => {
              const shown = i < revealCount;
              return (
                <li key={i} data-testid={`tc-item-${i}`}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 14,
                    padding: '14px 18px', borderRadius: 12,
                    background: shown ? SURFACE2 : 'transparent',
                    border: `1px solid ${shown ? BORDER : 'transparent'}`,
                    opacity: shown ? 1 : 0,
                    transform: shown ? 'translateY(0)' : 'translateY(6px)',
                    transition: 'opacity .35s ease, transform .35s ease',
                    minHeight: 52,
                  }}>
                  <div style={{
                    width: 6, height: 6, borderRadius: '50%', background: CLAY,
                    marginTop: 12, flexShrink: 0,
                  }} aria-hidden="true" />
                  <div style={{ fontSize: 17, lineHeight: 1.55, color: CREAM }}>{it}</div>
                </li>
              );
            })}
          </ul>
          {atEnd && (
            <div style={{ marginTop: 32, fontSize: 13, color: MUTED, fontStyle: 'italic' }} data-testid="tc-deck-complete">
              End of deck — press ← to review or Esc to return to the picker.
            </div>
          )}
        </div>
      </div>

      <div onClick={(e) => e.stopPropagation()}
        style={{ padding: '12px 20px', borderTop: `1px solid ${BORDER}`, background: SURFACE, cursor: 'default' }}>
        <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
          {deck.slides.map((_, i) => (
            <div key={i} style={{
              flex: 1, height: 3, borderRadius: 2,
              background: i < slideIdx ? CLAY : i === slideIdx ? 'rgba(196,90,59,0.6)' : 'rgba(245,242,235,0.08)',
            }} />
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: MUTED }}>
          <span>Click / Space / → to advance · ← to step back · Esc to exit</span>
          <div style={{ display: 'inline-flex', gap: 14, alignItems: 'center' }}>
            {session && (
              <button onClick={onEndSession} data-testid="tc-end-session-inline"
                style={{ background: 'transparent', border: 0, color: MUTED, cursor: 'pointer', fontSize: 11, textDecoration: 'underline' }}>
                End live session
              </button>
            )}
            <button onClick={resetSlide} data-testid="tc-reset-slide"
              style={{ background: 'transparent', border: 0, color: MUTED, cursor: 'pointer', fontSize: 11, textDecoration: 'underline' }}>
              Reset slide
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Root component ─────────────
const TeachersCorner = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('picker'); // 'picker' | 'present' | 'edit'
  const [activeDeck, setActiveDeck] = useState(null);
  const [editState, setEditState] = useState(null); // { initial, existingId }
  const [customDecks, setCustomDecks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [session, setSession] = useState(null);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef(null);

  const loadCustomDecks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/tc/decks`, { headers: authHeaders() });
      setCustomDecks(res.data || []);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCustomDecks(); }, [loadCustomDecks]);

  // Rehydrate any active session that survived a navigation (e.g. teacher
  // launched the OL demo room and came back to Teacher's Corner via Back).
  useEffect(() => {
    const stashedCode = sessionStorage.getItem('tc_session_code');
    if (!stashedCode) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/tc/session/${stashedCode}`, { headers: authHeaders() });
        if (res.data?.active) setSession(res.data);
        else sessionStorage.removeItem('tc_session_code');
      } catch (e) {
        sessionStorage.removeItem('tc_session_code');
      }
    })();
  }, []);

  const openBase = (d) => { setActiveDeck(d); setMode('present'); };
  const openCustom = (d) => {
    // custom deck shape: {id, name, tagline, slides, sourceDeckId}. Map to deck-like shape used by Presentation.
    setActiveDeck({ id: `custom:${d.id}`, title: d.name, tagline: d.tagline, slides: d.slides });
    setMode('present');
  };
  const editBase = (d) => {
    setEditState({ initial: cloneDeckForEdit(d), existingId: null });
    setMode('edit');
  };
  const editCustom = (d) => {
    setEditState({
      initial: { name: d.name, sourceDeckId: d.sourceDeckId, tagline: d.tagline, slides: d.slides },
      existingId: d.id,
    });
    setMode('edit');
  };
  const deleteCustom = async (d) => {
    if (!window.confirm(`Delete "${d.name}"? This can\u2019t be undone.`)) return;
    try {
      await axios.delete(`${API}/api/tc/decks/${d.id}`, { headers: authHeaders() });
      loadCustomDecks();
    } catch (e) { alert('Delete failed.'); }
  };

  const startSession = useCallback(async () => {
    try {
      const initial = activeDeck || BASE_DECKS[0];
      const isCustom = String(initial.id || '').startsWith('custom:');
      const payload = {
        deck_id: initial.id,
        slide_idx: 0,
        reveal_count: 0,
        deck_payload: isCustom ? {
          id: initial.id, title: initial.title, tagline: initial.tagline || '', slides: initial.slides,
        } : null,
      };
      const res = await axios.post(`${API}/api/tc/session`, payload, { headers: authHeaders() });
      setSession(res.data);
      try { sessionStorage.setItem('tc_session_code', res.data.code); } catch (_) {}
    } catch (e) {
      alert('Could not start a live session. Try again.');
    }
  }, [activeDeck]);

  const broadcast = useCallback(async (deck, slideIdx, revealCount) => {
    if (!session) return;
    try {
      const isCustom = String(deck.id || '').startsWith('custom:');
      await axios.put(`${API}/api/tc/session/${session.code}/state`, {
        deck_id: deck.id,
        slide_idx: slideIdx,
        reveal_count: revealCount,
        deck_payload: isCustom ? { id: deck.id, title: deck.title, tagline: deck.tagline || '', slides: deck.slides } : null,
      }, { headers: authHeaders() });
    } catch (e) { /* noop */ }
  }, [session]);

  const endSession = useCallback(async () => {
    if (!session) return;
    try { await axios.delete(`${API}/api/tc/session/${session.code}`, { headers: authHeaders() }); } catch (_) {}
    try { sessionStorage.removeItem('tc_session_code'); } catch (_) {}
    setSession(null);
  }, [session]);

  const copyCode = useCallback(() => {
    if (!session) return;
    try {
      navigator.clipboard?.writeText(session.code);
      setCopied(true);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* noop */ }
  }, [session]);

  useEffect(() => () => { if (copiedTimer.current) clearTimeout(copiedTimer.current); }, []);

  if (mode === 'edit' && editState) {
    return (
      <Editor
        initial={editState.initial}
        existingId={editState.existingId}
        onCancel={() => { setEditState(null); setMode('picker'); }}
        onSaved={() => { setEditState(null); setMode('picker'); loadCustomDecks(); }}
      />
    );
  }

  if (mode === 'present' && activeDeck) {
    return (
      <Presentation
        deck={activeDeck}
        onClose={() => { setActiveDeck(null); setMode('picker'); }}
        session={session}
        onBroadcast={broadcast}
        onEndSession={endSession}
        copied={copied}
        onCopy={copyCode}
      />
    );
  }

  return (
    <DeckPicker
      customDecks={customDecks}
      loading={loading}
      onOpenBase={openBase}
      onOpenCustom={openCustom}
      onEditBase={editBase}
      onEditCustom={editCustom}
      onDeleteCustom={deleteCustom}
      onBack={() => navigate('/admin/dashboard')}
      session={session}
      onStartSession={startSession}
      onEndSession={endSession}
      copied={copied}
      onCopy={copyCode}
    />
  );
};

export default TeachersCorner;
